import {
  PROGRAM_DAY_KEYS,
  PROGRAM_SLOTS_PER_DAY,
  createEmptyProgramState,
} from "../model/programState";

const PDF_FORMAT_TYPE = "LIST";

const DAY_KEY_TO_DTO = Object.freeze({
  day1: "Day 1",
  day2: "Day 2",
  day3: "Day 3",
  day4: "Day 4",
  day5: "Day 5",
  day6: "Day 6",
  day7: "Day 7",
});

const DTO_DAY_TO_KEY = Object.freeze(
  Object.entries(DAY_KEY_TO_DTO).reduce(
    (result, [dayKey, dtoDay]) => ({
      ...result,
      [dtoDay]: dayKey,
    }),
    {}
  )
);

const notesDtoToState = (notes) => {
  if (Array.isArray(notes)) return notes.join("\n");
  if (typeof notes === "string") return notes;

  return "";
};

const notesStateToDto = (notes) => {
  if (typeof notes !== "string" || notes === "") return [];

  return notes.split("\n");
};

const buildDayLabelsDto = (programState) =>
  PROGRAM_DAY_KEYS.reduce((labels, dayKey) => {
    const label = programState?.days?.[dayKey]?.label;
    if (!label) return labels;

    return {
      ...labels,
      [DAY_KEY_TO_DTO[dayKey]]: label,
    };
  }, {});

const buildProgramExercisesDto = (programState) =>
  PROGRAM_DAY_KEYS.reduce((programExercises, dayKey) => {
    const slots = programState?.days?.[dayKey]?.slots;
    if (!Array.isArray(slots)) return programExercises;

    const dayExercises = slots.reduce((exercises, slot, slotIndex) => {
      if (!slot || slot.exerciseId === null || slot.exerciseId === undefined) {
        return exercises;
      }

      return [
        ...exercises,
        {
          exerciseId: slot.exerciseId,
          sets: slot.sets ?? "",
          reps: slot.reps ?? "",
          intensity: slot.intensity ?? "",
          restTime: slot.rest ?? "",
          tempo: slot.tempo ?? "",
          notes: slot.notes ?? "",
          day: DAY_KEY_TO_DTO[dayKey],
          position: slotIndex + 1,
        },
      ];
    }, []);

    return [...programExercises, ...dayExercises];
  }, []);

const buildDto = (programState, emptyDateValue) => ({
  title: programState?.title ?? "",
  clientName: programState?.clientName ?? "",
  startDate: programState?.startDate || emptyDateValue,
  endDate: programState?.endDate || emptyDateValue,
  pdfFormatType: PDF_FORMAT_TYPE,
  dayLabels: buildDayLabelsDto(programState),
  notes: notesStateToDto(programState?.notes),
  programExercises: buildProgramExercisesDto(programState),
});

export const programDtoToState = (dto) => {
  const source = dto && typeof dto === "object" ? dto : {};
  const programState = createEmptyProgramState();

  programState.id = source.id ?? null;
  programState.title = source.title || "";
  programState.clientName = source.clientName || "";
  programState.startDate = source.startDate || "";
  programState.endDate = source.endDate || "";
  programState.notes = notesDtoToState(source.notes);

  if (source.dayLabels && typeof source.dayLabels === "object") {
    Object.entries(source.dayLabels).forEach(([dtoDay, label]) => {
      const dayKey = DTO_DAY_TO_KEY[dtoDay];
      if (!dayKey) return;

      programState.days[dayKey].label = label || "";
    });
  }

  if (Array.isArray(source.programExercises)) {
    source.programExercises.forEach((programExercise) => {
      if (!programExercise || typeof programExercise !== "object") return;

      const dayKey = DTO_DAY_TO_KEY[programExercise.day];
      const position = Number(programExercise.position);

      if (
        !dayKey ||
        !Number.isInteger(position) ||
        position < 1 ||
        position > PROGRAM_SLOTS_PER_DAY
      ) {
        return;
      }

      programState.days[dayKey].slots[position - 1] = {
        exerciseId: programExercise.exerciseId ?? null,
        name: programExercise.exerciseName || "",
        sets: programExercise.sets || "",
        reps: programExercise.reps || "",
        rest: programExercise.restTime || "",
        intensity: programExercise.intensity || "",
        tempo: programExercise.tempo || "",
        notes: programExercise.notes || "",
      };
    });
  }

  return programState;
};

export const programStateToSaveDto = (programState) => buildDto(programState, null);

export const programStateToPdfDto = (programState) => {
  // Current PDF behavior keeps empty dates as strings; save normalizes them to null.
  return buildDto(programState, "");
};
